import React, { useCallback, useState, useEffect } from 'react';
import { Layout, Result } from 'antd';
import NackleMenu from '@nackle/menu';
import { Route, Link, routeHistory } from '@nackle/biw-web-router';
import { routeList, bundleRoutingMiddleware, redirectMiddleware, replacePatternVars } from './appRouting';
import { Bridge } from '@nackle/bridge';
import auth from './appAuth';
import {
    AppHeader,
    AppFooter,
    AppError,
    CMXService,
    applyRequestMiddleware,
    authRequestMiddleware,
    authRouteMiddleware
} from '@nackle/studio-tools';
import { setLanguageBundle } from '@nackle/intl-tools';
import { UnsavedChangesModal } from '@nackle/studio-tools';
import '@nackle/studio-tools/dist/studio-tools.css';
import './styles/app.less';
import './styles.less';

const { Content, Sider } = Layout;

export default function App() {
    const [ loaded, setLoaded ] = useState( false );
    const [ cmxBundleLoaded, setCMXBundleLoaded ] = useState( false );
    const [ accessToken, setAccessToken ] = useState( '' );
    const [ user, setUser ] = useState();

    const fetchTranslations = useCallback( async () => {
        if ( auth.state ) {
            // Prioritize whatever locale code is forced by the studio since we probably aren't ever going
            // to actually translate the studio. Fallback to user locale code.
            setUser( auth?.state?.claims );

            const localeCode =  auth.state.claims.locale || 'en-US';
            const cmxService = new CMXService( auth.state );
            const cmxBundle = await cmxService.getBundle( [ 'navigation', 'studio-in-context-help' ], localeCode ).catch( () => null );

            await setLanguageBundle( localeCode, cmxBundle );
        } else {
            console.error( 'Unable to fetch translations with missing or invalid auth state.', auth.state );
        }
    }, [] );
    
    const handleSignIn = async () => {
        await routeHistory.refresh();
        await fetchTranslations();
        
        setLoaded( true );
        setCMXBundleLoaded( true );
        setAccessToken( auth.state.access_token );
    };

    useEffect( () => {
        const storeAccessToken = ( state ) => setAccessToken( state?.access_token );
        const storeUser = ( state ) => setUser( state?.claims );
        auth.events.stateChange.on( storeAccessToken );
        auth.events.stateChange.on( storeUser );

        return () => {
            auth.events.stateChange.off( storeAccessToken );
            auth.events.stateChange.off( storeUser );
        };
    }, [] );
    
    const handleCompanyChange = useCallback( ( route ) => ( company ) => {
        if ( route?.match?.pattern === '/' ) {
            routeHistory.push( `/${ company.id }` );
        }
        
        if ( route?.match ) {
            routeHistory.push(
                replacePatternVars( route?.match?.pattern, { ...route?.state?.params, companyId: company.id } )
            );
        } else {
            routeHistory.push( `/${ company.id }` );
        }
    }, [] );
    
    return (
        <AppError>
            <Route render={ ( route ) => {
                // set the title
                document.title = route && route.match ? route.match.title : 'Home';
                const companyId = route.state?.params?.companyId;
                const pathname = window.location.pathname;
                let disableCompanySelect = false;

                return (
                    <Bridge
                        auth={ auth }
                        onSignIn={ handleSignIn }
                    >
                        { loaded &&
                            <Layout className="root-layout">
                                <AppHeader
                                    auth={ auth }
                                    companyId={ companyId }
                                    onCompanyChange={ handleCompanyChange( route ) }
                                    companySelectDisabled={ disableCompanySelect }
                                />
                                <Layout>
                                    <Sider>
                                        <NackleMenu
                                            auth={ auth }
                                            cmxBundleLoaded={ cmxBundleLoaded }
                                        />
                                    </Sider>
                                    <Layout className="page-layout">
                                        <Content className="app-content">
                                            <div className="scrollable-content">
                                                <div className="app-content-body">
                                                    {
                                                        route.match ?
                                                            React.cloneElement( route.component, { companyId, accessToken, user, pathname } )
                                                            : (
                                                                <Result
                                                                    status="404"
                                                                    title="404"
                                                                    subTitle="Sorry, the page you visited does not exist."
                                                                    extra={ <Link to="/" className="nkl-btn nkl-btn-primary">Back Home</Link> }
                                                                />
                                                            )
                                                    }
                                                </div>
                                                <AppFooter className="app-content-footer" />
                                            </div>
                                        </Content>
                                    </Layout>
                                </Layout>
                            </Layout>
                        }
                    </Bridge>
                );
            } } />
        </AppError>
    );
}

// Connect Routing
routeHistory.applyRoutes( routeList );
routeHistory.applyMiddleware( [
    authRouteMiddleware( auth ),
    bundleRoutingMiddleware,
    UnsavedChangesModal.routerMiddleware,
    ...redirectMiddleware
] );

// Connect Auth to Fetch
applyRequestMiddleware( authRequestMiddleware( auth ) );
